
import React, { useState, useMemo, useEffect } from 'react';
import { TajwidRule } from '../types';

interface RulesViewProps {
  rules: TajwidRule[];
}

const RulesView: React.FC<RulesViewProps> = ({ rules }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Scroll to top when category changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCategory]);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(rules.map(r => r.category)));
    return unique.sort().map(cat => ({
      code: cat,
      name: rules.find(r => r.category === cat)?.categoryName || "Unknown"
    }));
  }, [rules]);

  const filteredRules = useMemo(() => {
    if (!selectedCategory) return [];
    return rules.filter(r => r.category === selectedCategory);
  }, [selectedCategory, rules]);

  // Helper untuk animasi performa tinggi
  // will-change: transform memberitahu browser untuk memindahkan elemen ke layer GPU
  // Delay di-cap maksimal 400ms agar user tidak menunggu terlalu lama pada list panjang
  const getAnimationProps = (index: number) => ({
    style: {
      animationDelay: `${Math.min(index * 40, 400)}ms`,
      willChange: 'transform, opacity'
    }
  });

  if (!selectedCategory) {
    return (
      <div key="category-list" className="p-4 md:p-8 animate-scaleIn">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-black text-dark mb-2 uppercase tracking-tighter">HUKUM TAJWID</h2>
          <p className="text-primary text-sm font-medium">Klik kategori untuk melihat detail materi</p>
          <div className="h-1 w-20 bg-primary mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
          {categories.map((cat, idx) => (
            <button
              key={cat.code}
              onClick={() => setSelectedCategory(cat.code)}
              {...getAnimationProps(idx)}
              className="group bg-white p-5 md:p-6 rounded-2xl border-2 border-stroke hover:border-primary hover:shadow-xl transition-all duration-300 text-left flex items-center gap-4 animate-slideUp"
            >
              <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-primary/10 text-primary rounded-xl flex items-center justify-center font-black text-lg md:text-2xl group-hover:bg-primary group-hover:text-white transition-all duration-300 shadow-sm transform group-hover:scale-110 group-active:scale-95">
                {cat.code.split('_')[0]}
              </div>
              <div className="min-w-0">
                <h3 className="font-black text-dark text-sm md:text-base group-hover:text-primary transition-colors uppercase truncate">{cat.name}</h3>
                <p className="text-[10px] text-body font-bold uppercase tracking-widest mt-0.5 group-hover:text-primary/70 transition-colors">
                  {rules.filter(r => r.category === cat.code).length} Aturan
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div key="detail-view" className="p-4 md:p-8 min-h-screen">
      {/* Header Sticky Bar */}
      <div className="sticky top-20 z-30 bg-white/90 backdrop-blur-sm py-2 -mx-4 px-4 md:-mx-8 md:px-8 mb-6 border-b border-stroke animate-fadeIn">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setSelectedCategory(null)}
            className="p-2.5 bg-white hover:bg-primary hover:text-white rounded-xl text-body transition-all border border-stroke shadow-sm active:scale-90"
            aria-label="Kembali ke menu kategori"
          >
            <i className="fa-solid fa-arrow-left text-lg"></i>
          </button>
          <div className="min-w-0">
            <span className="bg-primary/10 text-primary text-[9px] font-black px-2 py-0.5 rounded-full uppercase mb-0.5 block w-fit animate-pulse">KATEGORI {selectedCategory?.split('_')[0]}</span>
            <h2 className="text-lg md:text-xl font-black text-dark uppercase tracking-tighter truncate">
              {categories.find(c => c.code === selectedCategory)?.name}
            </h2>
          </div>
        </div>
      </div>

      <div className="space-y-12 pb-10">
        {filteredRules.map((rule, idx) => (
          <div 
            key={`${selectedCategory}-${idx}`} 
            {...getAnimationProps(idx)}
            className="bg-white rounded-3xl overflow-hidden border border-stroke shadow-xl animate-slideUp"
          >
            <div className="bg-primary px-6 py-4 flex flex-wrap items-center justify-between gap-3">
               <div className="flex items-center gap-3">
                 <div className="w-8 h-8 bg-white/20 text-white rounded-lg flex items-center justify-center font-black text-sm backdrop-blur-md shadow-inner">
                   {idx + 1}
                 </div>
                 <h3 className="text-lg md:text-xl font-black text-white uppercase tracking-tight">{rule.nama}</h3>
               </div>
               <span className="text-white font-bold italic text-xs bg-white/10 px-3 py-1 rounded-full border border-white/10">"{rule.arti}"</span>
            </div>

            <div className="p-5 md:p-8 space-y-8">
              {/* Syarat & Definisi Section */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 space-y-6">
                  <div>
                    <h4 className="text-[10px] font-black text-primary uppercase tracking-widest mb-3 flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full bg-primary animate-ping"></div> Definisi Materi
                    </h4>
                    <div className="text-dark text-sm leading-relaxed bg-bg p-5 rounded-2xl border border-stroke">
                      {rule.syarat.split('.').map((sentence, sIdx) => {
                        const s = sentence.trim();
                        if (!s) return null;
                        
                        const isDefinition = s.toLowerCase().includes('artinya');
                        
                        return (
                          <div key={sIdx} className={`mb-3 last:mb-0 ${isDefinition ? 'flex items-start gap-2' : ''}`}>
                            {isDefinition && <div className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 bg-primary rounded-full"></div>}
                            <p className={isDefinition ? "font-bold text-primary" : "font-medium"}>
                              {s}.
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-[10px] font-black text-primary uppercase tracking-widest mb-3 flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full bg-primary"></div> Cara Membaca
                    </h4>
                    <div className="p-5 bg-primary/5 rounded-2xl border-l-4 border-primary shadow-sm hover:bg-primary/10 transition-colors">
                      <p className="text-dark font-bold text-sm md:text-base leading-snug">{rule.caraBaca}</p>
                    </div>
                  </div>
                </div>

                {/* Huruf Section */}
                <div className="lg:col-span-4 flex flex-col">
                  <div className="bg-dark rounded-3xl p-6 text-white shadow-xl flex-grow flex flex-col justify-center relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-3xl group-hover:bg-primary/30 transition-all"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-meta/20 rounded-full blur-3xl"></div>
                    
                    <h4 className="text-[9px] font-black text-primary uppercase tracking-[0.2em] mb-6 text-center relative z-10">Huruf Hijaiyah</h4>
                    <div className="grid grid-cols-4 lg:grid-cols-2 gap-2 relative z-10">
                      {rule.huruf.map((h, i) => (
                        <div key={i} className="bg-white/10 aspect-square rounded-xl flex items-center justify-center arabic-font text-2xl text-white border border-white/5 shadow-inner hover:scale-110 hover:bg-white/20 transition-all cursor-default">
                          {h}
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/10 text-center relative z-10">
                      <p className="text-[10px] text-body font-black uppercase tracking-widest">{rule.huruf.length} Huruf</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contoh Section */}
              <div>
                <h4 className="text-[10px] font-black text-primary uppercase tracking-widest mb-4 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-primary"></div> Contoh Bacaan
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {rule.contoh.map((c, i) => (
                    <div key={i} className="p-5 bg-white rounded-2xl border border-stroke shadow-sm hover:shadow-lg hover:border-primary/50 transition-all group cursor-pointer">
                      <p className="arabic-font text-xl md:text-2xl text-right mb-3 text-dark group-hover:text-primary transition-colors leading-loose" dir="rtl">{c.teks}</p>
                      <div className="pt-3 border-t border-stroke flex justify-between items-center">
                        <div className="min-w-0 pr-2">
                          <p className="text-[10px] md:text-xs font-black text-primary tracking-tight uppercase truncate">{c.latin}</p>
                          <p className="text-[9px] md:text-[10px] text-body font-bold italic truncate">{c.keterangan}</p>
                        </div>
                        <div className="text-stroke group-hover:text-primary transition-colors transform group-hover:rotate-12 flex-shrink-0">
                          <i className="fa-solid fa-volume-high"></i>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RulesView;
